import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { HereAndNowTab } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { Icon } from "@atoms";
import { cn } from "@utils";

import { useHaNTabLinkSearch } from "../data/useHaNBuilding";
import { useAvailableTabs } from "../useHaNTabs";
import { HaNContext } from "./HaNContext";

// Explore has its own search bar in this spot, so the tabs only show on rooms and workspaces
export const HaNTabs = () => {
  const { tab, mapViewApiRef } = useRequiredContext(HaNContext);
  const availableTabs = useAvailableTabs();
  const search = useHaNTabLinkSearch();

  const fitFloorInView = () => {
    mapViewApiRef.current?.fitBuildingNodes({ nodeIds: [], abortIfAlreadyInView: true });
  };

  if (tab === "explore") return null;

  return (
    <nav className="flex h-[60px] justify-around gap-x-4 px-5">
      {availableTabs.map(availableTab => (
        <HaNTabLink
          key={availableTab}
          tab={availableTab}
          search={search}
          onClick={availableTab === tab ? undefined : fitFloorInView}
        />
      ))}
    </nav>
  );
};

const HaNTabLink = ({ tab, search, onClick }: { tab: HereAndNowTab; search: string; onClick?: () => void }) => {
  const { t } = useTranslation();

  if (tab === "explore") {
    return (
      <NavLink
        to={{ pathname: "/now/explore", search }}
        onClick={onClick}
        className="flex items-center self-center px-1.5 py-3 text-mapiq-black-400 hover:text-mapiq-black-800* focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mapiq-black-900">
        <Icon
          icon="search"
          weight="bold"
          size="20px"
          color="currentColor"
        />
      </NavLink>
    );
  }

  return (
    <NavLink
      to={{ pathname: `/now/${tab}`, search }}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex flex-1 items-center justify-center border-b-2 border-transparent font-normal text-mapiq-black-500 no-underline hover:text-mapiq-black-800* hover:no-underline focus-visible:text-mapiq-black-800* focus-visible:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mapiq-black-900",
          { "border-mapiq-black-800* font-semibold text-mapiq-black-800*": isActive },
        )
      }>
      {tab === "rooms" ? t("hereAndNow:Rooms") : t("hereAndNow:Workspaces")}
    </NavLink>
  );
};
